import {Component, inject} from '@angular/core';
import {BaseNodeGridComponent} from '../../base-components/base-node-grid.component';
import {TreeHelperService} from '../../../service/helper/tree-helper.service';
import {WimTreeNode} from '../../../model/wim-tree.node';
import {NodeType} from '../../../constants/node-type';
import {TableLazyLoadEvent, TableModule} from 'primeng/table';
import {ToolLocationListItem} from '../../../model/tool-location-list-item.model';
import {ToolLocationService} from '../../../service/tool-location.service';
import {SharedModule, TreeNode} from 'primeng/api';
import {WimIcons} from '../../../constants/wim-icons';
import {CantaaErrorHandlerService} from '../../../service/cantaa-error-handler.service';
import {MatButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {RouteType} from '../../../constants/route-type';
import {CantaaMessageService} from '../../../service/cantaa-message.service';
import {ToolLocationDocumentService} from "../../../service/tool-location-document.service";
import {Permission} from '../../../constants/Permission';
import {HasPermissionDirective} from '../../../directive/has-permission.directive';
import {UuidHelperService} from '../../../service/helper/uuid-helper.service';
import {TranslateModule} from '@ngx-translate/core';
import {GridRequest} from '../../../model/grid-request.model';
import {SearchInputComponent} from '../../inputs/search-input/search-input.component';
import {ShowIfDebugMode} from "../../../directive/show-if-debug.directive";
import * as XLSX from 'xlsx';

@Component({
  selector: 'wim-location-grid',
  standalone: true,
  imports: [
    SharedModule,
    TableModule,
    MatButton,
    MatIcon,
    HasPermissionDirective,
    TranslateModule,
    SearchInputComponent,
    ShowIfDebugMode
  ],
  templateUrl: './tool-location-grid.component.html',
  styleUrl: './tool-location-grid.component.scss'
})
export class ToolLocationGrid extends BaseNodeGridComponent<ToolLocationListItem> {
  protected readonly Permission = Permission;
  protected override nodeTypes = [
    NodeType.EMPLOYEE_RELATION,
    NodeType.MOBILE_LOCATION_RELATION,
    NodeType.TOOL_LOCATION_RELATION
  ];

  private currentSelectedNode: WimTreeNode | null = null;

  public toolLocationDocumentService = inject(ToolLocationDocumentService);
  private messageService = inject(CantaaMessageService);
  private treeHelperService = inject(TreeHelperService);
  private errorHandler = inject(CantaaErrorHandlerService);
  private toolLocationService = inject(ToolLocationService);

  override async fetchData(selectedNode: WimTreeNode, lazyLoadMetadata: TableLazyLoadEvent) {
    await super.fetchData(selectedNode, lazyLoadMetadata);

    this.gridItems.forEach(toolLocation => {
      this.toolLocationDocumentService.getDefaultPhotoScaled35x35AndRender(toolLocation.id);
    });
  }

  override async fetchGridItems(gridRequest: GridRequest, selectedNode: WimTreeNode) {
    this.currentSelectedNode = selectedNode;
    try {
      return await this.toolLocationService.getToolLocations(gridRequest, selectedNode);
    } catch (e) {
      this.errorHandler.handleError(e, 'FAILED_TO_FETCH');
      throw e;
    }
  }

  override mapTreeItems(selectedNode: WimTreeNode, items: ToolLocationListItem[]): WimTreeNode[] {
    return items
      .map<WimTreeNode>(listItem => {

        let treeNode = {
          id: listItem.id,
          key: UuidHelperService.generateUuid(),
          label: listItem.locationPretty,
          data: `${listItem.id};${listItem.locationTypeCode}`,
          icon: WimIcons.TOOL_LOCATION_ICON,
          nodeType: NodeType.TOOL_LOCATION,
          route: RouteType.LOCATION,
          children: [] as TreeNode[],
          leaf: false,
          parent: selectedNode
        };
        listItem.treeNode = treeNode;

        treeNode.children = [
          this.treeHelperService.createTransactionRelationsTreeItem(treeNode),
          this.treeHelperService.createToolRelationsTreeItem(treeNode),
          this.treeHelperService.createProjectRelationsTreeItem(treeNode)
        ];

        return treeNode;
      });
  }

  protected readonly location = location;

  async delete() {
    try {
      await this.toolLocationService.deleteLocation(this.selectedItem!);
      this.messageService.success('DELETE.SUCCESS');
      await this.reFetchData();
    } catch (e) {
      throw this.errorHandler.handleError(e, 'DELETE.FAILED');
    }
  }


  async exportToExcel() {
    try {
      // 0. Guard-Clause für schnellen Abbruch bei fehlenden Daten
      if (!this.currentSelectedNode) {
        this.messageService.error('Bitte wählen Sie zuerst einen Standort-Knoten aus.');
        return;
      }

      // Live-Sortierung direkt aus der PrimeNG-Table-Komponente auslesen
      // Falls das Array in der Komponente existiert, nutzen wir das, ansonsten Fallback auf gridMeta
      const liveMultiSort = this.dataTable?.multiSortMeta || this.gridMeta?.lazyLoadMeta?.multiSortMeta;

      // 1. GridRequest für den Backend-Export aufbauen
      const exportRequest: GridRequest = {
        pageRequest: {
          pageNumber: 0,
          pageSize: 999999
        },
        // Hier nutzen wir nun die Live-Sortierung für das Backend
        sortDefinitions: liveMultiSort && liveMultiSort.length > 0
          ? liveMultiSort.map((meta: any, i: number) => ({
            sortBy: meta.field,
            sortOrder: meta.order === 1 ? 'ASC' : 'DESC',
            index: i
          }))
          : this.gridMeta?.lazyLoadMeta?.sortField
            ? [{
              sortBy: Array.isArray(this.gridMeta.lazyLoadMeta.sortField)
                ? this.gridMeta.lazyLoadMeta.sortField[0]
                : this.gridMeta.lazyLoadMeta.sortField,
              sortOrder: this.gridMeta.lazyLoadMeta.sortOrder === 1 ? 'ASC' : 'DESC',
              index: 0
            }]
            : [],
        filterDefinitions: this.gridMeta?.filters
          ? Object.keys(this.gridMeta.filters)
            .map(key => {
              const rawFilter = this.gridMeta.filters![key];
              if (!rawFilter) return null;

              const filters = Array.isArray(rawFilter) ? rawFilter : [rawFilter];
              const active = filters.find(f => f?.value !== undefined && f?.value !== null && String(f.value).trim() !== '');

              if (!active) return null;

              return {
                propertyName: key,
                value: String(active.value),
                filterOperator: active.matchMode || 'contains'
              };
            })
            .filter((f): f is { propertyName: string; value: string; filterOperator: string } => f !== null)
          : undefined,
        searchQuery: this.gridMeta?.searchQuery
      };

      // 2. Daten laden
      const response = await this.toolLocationService.getToolLocations(exportRequest, this.currentSelectedNode);

      const allItems: any[] = response && 'items' in response && Array.isArray((response as any).items)
        ? (response as any).items
        : Array.isArray(response) ? response : [];

      if (!allItems.length) {
        this.messageService.error('Keine Daten zum Exportieren vorhanden.');
        return;
      }

      // 3. Spalten-Mapping für Excel vorbereiten
      const excelData = allItems.map((item: ToolLocationListItem) => ({
        'ID': item.id,
        'Standort': item.locationPretty || '',
        'RFID-Nummer': item.rfid || '',
        'E-Mail': item.email || '',
        'Mitarbeiter': item.employeePretty || ''
      }));

      // 4. DYNAMISCHE FRONTEND-SORTIERUNG ALS SICHERHEITSNETZ
      if (liveMultiSort && liveMultiSort.length > 0) {
        const primarySort = liveMultiSort[0]; // Die vom Benutzer aktiv geklickte Spalte

        const columnMapping: { [key: string]: string } = {
          'id': 'ID',
          'locationPretty': 'Standort',
          'rfid': 'RFID-Nummer',
          'email': 'E-Mail',
          'employeePretty': 'Mitarbeiter'
        };

        const excelKey = columnMapping[primarySort.field];

        if (excelKey) {
          excelData.sort((a: any, b: any) => {
            const valueA = String(a[excelKey] ?? '');
            const valueB = String(b[excelKey] ?? '');

            if (excelKey === 'ID') {
              const numA = parseInt(valueA, 10) || 0;
              const numB = parseInt(valueB, 10) || 0;
              return primarySort.order === 1 ? numA - numB : numB - numA;
            }

            return primarySort.order === 1
              ? valueA.localeCompare(valueB, undefined, { numeric: true, sensitivity: 'base' })
              : valueB.localeCompare(valueA, undefined, { numeric: true, sensitivity: 'base' });
          });
        }
      }

      // 5. Excel generieren
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(excelData, {
        header: ['ID', 'Standort', 'RFID-Nummer', 'E-Mail', 'Mitarbeiter']
      });

      const workbook: XLSX.WorkBook = {
        Sheets: {'Standorte': worksheet},
        SheetNames: ['Standorte']
      };

      const dateStr = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `Standorte_Export_${dateStr}.xlsx`);

      this.messageService.success('Excel-Export erfolgreich erstellt.');

    } catch (error) {
      this.errorHandler.handleError(error, 'EXPORT.FAILED');
    }
  }

}
